"use client";

// Section animée de la fiche Hermes Agent : la boucle d'apprentissage.
//
// Ce que l'agent a de singulier, c'est qu'il écrit lui-même la fiche de ce
// qu'il vient de faire, puis la réutilise. Le raconter ne suffit pas, alors on
// le fait vivre : le visiteur relance trois fois la même tâche et voit la durée
// s'effondrer, avec la compétence enregistrée au passage.
//
// Chaque passage simule un travail (barre de progression) puis affiche son
// résultat. Le mouvement reste dans les bornes maison : fondu, translation
// courte, pas de rebond. `useReducedMotion` coupe la simulation d'attente pour
// qui a désactivé les animations : le résultat s'affiche immédiatement.

import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

// Trois passages : découverte, fiche écrite, fiche appliquée.
const RUNS = [
    { duration: "Ha_skills_r1_time", step: "Ha_skills_r1_step", learned: true },
    { duration: "Ha_skills_r2_time", step: "Ha_skills_r2_step", learned: false },
    { duration: "Ha_skills_r3_time", step: "Ha_skills_r3_step", learned: false },
];

const WORK_DURATION_MS = 1400;

export default function HaSkills() {
    const t = useTranslations();
    const reduceMotion = useReducedMotion();

    // Nombre de passages terminés, et passage en cours (null si au repos).
    const [done, setDone] = useState(0);
    const [running, setRunning] = useState(false);
    const timeout = useRef(null);

    // Un démontage pendant la simulation laisserait le timer courir et
    // provoquerait un setState sur un composant démonté.
    useEffect(() => {
        return () => {
            if (timeout.current) clearTimeout(timeout.current);
        };
    }, []);

    const launch = () => {
        if (running || done >= RUNS.length) return;

        if (reduceMotion) {
            setDone((value) => value + 1);
            return;
        }

        setRunning(true);
        timeout.current = setTimeout(() => {
            setRunning(false);
            setDone((value) => value + 1);
        }, WORK_DURATION_MS);
    };

    const reset = () => {
        if (timeout.current) clearTimeout(timeout.current);
        setRunning(false);
        setDone(0);
    };

    const isComplete = done >= RUNS.length;

    return (
        <section id="ha-skills" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Ha_skills_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Ha_skills_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10 p-6 sm:p-10">
                {/* Glows (indigo Hermes) */}
                <div className="absolute -top-24 -left-20 w-[420px] h-[420px] bg-indigo-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -right-20 w-[380px] h-[380px] bg-violet-500/10 rounded-full blur-3xl pointer-events-none" />

                <div className="relative">
                    {/* La tâche */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-start gap-3">
                            <div className="w-11 h-11 rounded-xl bg-indigo-500/15 ring-1 ring-indigo-400/30 flex items-center justify-center shrink-0">
                                <SolarIcon
                                    name="inbox-in-bold-duotone"
                                    className="w-5 h-5 text-indigo-300"
                                />
                            </div>
                            <div>
                                <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-indigo-300">
                                    {t("Ha_skills_task_label")}
                                </span>
                                <p className="text-[15px] sm:text-base font-medium text-white leading-snug mb-0 mt-1">
                                    {t("Ha_skills_task")}
                                </p>
                            </div>
                        </div>

                        <button
                            onClick={isComplete ? reset : launch}
                            disabled={running}
                            className={`shrink-0 rounded-full px-5 py-2.5 text-[13px] font-semibold transition-colors duration-200 ${
                                running
                                    ? "bg-white/[0.06] text-gray-400 ring-1 ring-white/10 cursor-default"
                                    : isComplete
                                      ? "bg-white/[0.08] text-gray-200 ring-1 ring-white/15 hover:bg-white/[0.12]"
                                      : "bg-indigo-500 text-white ring-1 ring-indigo-400/60 hover:bg-indigo-400"
                            }`}
                        >
                            {running
                                ? t("Ha_skills_working")
                                : isComplete
                                  ? t("Ha_skills_reset")
                                  : done === 0
                                    ? t("Ha_skills_run")
                                    : t("Ha_skills_again")}
                        </button>
                    </div>

                    {/* Barre de travail en cours */}
                    {running && (
                        <div className="mt-6 h-1.5 w-full rounded-full bg-white/[0.08] overflow-hidden">
                            <motion.div
                                initial={{ width: "0%" }}
                                animate={{ width: "100%" }}
                                transition={{
                                    duration: WORK_DURATION_MS / 1000,
                                    ease: "easeInOut",
                                }}
                                className="h-full rounded-full bg-gradient-to-r from-indigo-400 to-violet-300"
                            />
                        </div>
                    )}

                    {/* Passages terminés */}
                    <div className="mt-6 flex flex-col gap-3">
                        {RUNS.slice(0, done).map((run, index) => (
                            <motion.div
                                key={run.duration}
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.35, ease: "easeOut" }}
                                className="rounded-2xl bg-white/[0.04] ring-1 ring-white/10 px-5 py-4"
                            >
                                <div className="flex items-center justify-between gap-4">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <SolarIcon
                                            name="check-circle-bold-duotone"
                                            className="w-5 h-5 text-emerald-400 shrink-0"
                                        />
                                        <div className="min-w-0">
                                            <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-gray-500">
                                                {t("Ha_skills_run_label", {
                                                    number: index + 1,
                                                })}
                                            </span>
                                            <p className="text-[15px] text-gray-100 leading-snug mb-0 mt-0.5">
                                                {t(run.step)}
                                            </p>
                                        </div>
                                    </div>
                                    <span className="shrink-0 text-base sm:text-lg font-bold text-white tabular-nums">
                                        {t(run.duration)}
                                    </span>
                                </div>

                                {run.learned && (
                                    <div className="mt-3 flex items-center gap-2 rounded-xl bg-indigo-500/15 ring-1 ring-indigo-400/30 px-3.5 py-2.5">
                                        <SolarIcon
                                            name="notebook-bookmark-bold-duotone"
                                            className="w-4 h-4 text-indigo-300 shrink-0"
                                        />
                                        <span className="text-[13px] font-medium text-indigo-100">
                                            {t("Ha_skills_learned")}
                                        </span>
                                    </div>
                                )}
                            </motion.div>
                        ))}
                    </div>

                    {/* Conclusion, une fois les trois passages joués */}
                    {isComplete && (
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, ease: "easeOut" }}
                            className="mt-5 rounded-2xl bg-white/[0.06] ring-1 ring-white/10 px-5 py-4"
                        >
                            <p className="text-[15px] sm:text-base text-gray-100 leading-snug mb-0">
                                {t.rich("Ha_skills_done", {
                                    b: (chunks) => (
                                        <strong className="text-white font-semibold">
                                            {chunks}
                                        </strong>
                                    ),
                                })}
                            </p>
                        </motion.div>
                    )}
                </div>
            </div>

            <p className="mt-6 text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Ha_skills_caption")}
            </p>
        </section>
    );
}
